
import Vue from 'vue';
import {
  Color,
  Label,
  LabelRelatedTo,
  LabelWithStateAndSuggestions,
} from '@/model';
import {
  findLabelSuggestion,
  isNewlyAdded,
  LabelIconsForGlasses,
  LabelTypesForContextGlasses,
  LabelTypesForGlasses,
} from '@/store/labels';
import { distinctByFn } from '@/util/array-util';

export default Vue.extend({
  name: 'LabelWithSuggestionIcons',

  props: {
    label: { type: Object as () => LabelWithStateAndSuggestions, required: true },
    disabled: { type: Boolean, default: false },
    nonClickable: { type: Boolean, default: false },
    closable: { type: Boolean, default: false },
    discreetSuggestions: { type: Boolean, default: false },
    toggleable: { type: Boolean, default: false },
  },

  computed: {
    properties(): any {
      return {
        alpha: labelAlpha(this.label),
        searchHighlighedLabelCode: searchHighlighedLabelCode(this.label),
        labelColor: toLabelColor(this.label, labelAlpha(this.label)),
        suggestionLabelTitle: suggestionLabelTitle(this.label),
        darkenedIconColor: this.label.isRemoved ? darkenedRemovedIconColor(this.label) : darkenedIconColor(this.label),
        textColorBasedOnLuminance: textColorBasedOnLuminance(this.label, labelAlpha),
        isRelatedTo: this.label.relatedTo && this.label.relatedTo.length > 0,
        relatedToDescription: this.label.relatedTo ? relatedToDescription(this.label.relatedTo) : '',
        relatedToIcons: distinctByFn(this.label.relatedTo || [], (rl) => rl.label.id).map((rl) => ({
          icon: relatedToIcon(rl.index + 1, rl.label.type, this.label),
          color: relatedToIconColor(rl, this.label),
        })),
        isParentToIcon: this.label.isParentAtIndex ? relatedToIcon(this.label.isParentAtIndex, this.label.type, this.label) : undefined,
        warning: this.label.warning,
        mlSuggestion: findLabelSuggestion(this.label, 'ml'),
        relatedSuggestion: findLabelSuggestion(this.label, 'related'),
        mlSuggestionShowDetails: !this.discreetSuggestions,
        ingestSuggestion: findLabelSuggestion(this.label, 'ingest'),
        outOfContextSuggestion: findLabelSuggestion(this.label, 'outofcontext'),
        suggestionBgColor: this.discreetSuggestions ? textColorBasedOnLuminance(this.label, labelAlpha) : darkenedIconColor(this.label),
        suggestionTextColor: this.discreetSuggestions ? textColorBasedOnLuminance(this.label, labelAlpha) : undefined,
        suggestionIconColor: this.discreetSuggestions ? textColorBasedOnLuminance(this.label, iconAlpha) : darkenedIconColor(this.label),
        isNewlyAdded: isNewlyAdded(this.label),
      };
    },
  },
  methods: {
    onChipClicked(id: number, event?: MouseEvent) {
      this.$emit('onChipClicked', id, event);
    },
  },
});

const relatedToIconColor = (relatedTo: LabelRelatedTo, label: LabelWithStateAndSuggestions): string | undefined => {
  if (!relatedTo) {
    return undefined;
  }
  if (LabelTypesForGlasses.includes(relatedTo.label.type) && !LabelTypesForContextGlasses.includes(label.type)) {
    return darkenedIconColor(label);
  }
  return relatedTo.isRemoved || label.isRemoved ? darkenedRemovedIconColor(label) : darkenedIconColor(label);
};

const numToRelatedAlphaOrNum = (num: number): string => (num < 10 ? String(num) : String.fromCharCode(97 + num - 10));

const relatedToIcon = (num: number, type: string, thisLabel: LabelWithStateAndSuggestions) => {
  if (LabelTypesForGlasses.includes(type) && !LabelTypesForContextGlasses.includes(type)) {
    const suffix = thisLabel.type !== 'Product' ? '' : '-outline';
    return `${LabelIconsForGlasses.get(type) ?? 'mdi-circle-medium'}${suffix}`;
  }

  const suffix = type === 'Transaction' ? '' : '-outline';
  const alphaOrNum = numToRelatedAlphaOrNum(num);
  if (num < 10) {
    return `mdi-numeric-${alphaOrNum}-circle${suffix}`;
  } else if (num < 26 + 10) {
    return `mdi-alpha-${alphaOrNum}-circle${suffix}`;
  } else {
    return `mdi-circle-multiple${suffix}`;
  }
};

const relatedToDescription = (
  related: ReadonlyArray<{label: Label; index: number}>,
): string => {
  const relatedToStr = related.map((r) => {
    if (LabelTypesForGlasses.includes(r.label.type)) {
      return `${r.label.code}`;
    } else {
      return `${r.label.code} (${numToRelatedAlphaOrNum(r.index + 1).toUpperCase()})`;
    }
  }).join('\n');
  return `Related to:\n${relatedToStr}`;
};

const darkenedIconColor = (label: Label): string => (perceivedLuminance(label.color) > 160.0 ? 'rgba(0,0,0,0.54)' : 'rgba(255,255,255,0.7)');
const darkenedRemovedIconColor = (label: Label): string => (perceivedLuminance(label.color) > 160.0 ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.3)');

const textColorBasedOnLuminance = (
  label: LabelWithStateAndSuggestions,
  alphaFun: (label: Label) => number,
): string => (perceivedLuminance(label.color) > 160.0 ? `rgba(0,0,0,${alphaFun(label)})` : `rgba(255,255,255,${alphaFun(label)})`);

const perceivedLuminance = (c: Color): number => 0.299 * c.r + 0.587 * c.g + 0.114 * c.b;

const suggestionLabelTitle = (
  label: LabelWithStateAndSuggestions,
): string => {
  const mlSuggestion = findLabelSuggestion(label, 'ml');
  return mlSuggestion ? `${mlSuggestion.confidence}% match with the selected text` : 'suggestion';
};

const toLabelColor = (label: Label, alpha: number) => `rgba(${label.color.r}, ${label.color.g}, ${label.color.b}, ${alpha})`;

const labelAlpha = (label: LabelWithStateAndSuggestions) => (label.isRemoved ? 0.5 : 1);
const iconAlpha = (label: LabelWithStateAndSuggestions) => (label.isRemoved ? 0.4 : 0.54);

const searchHighlighedLabelCode = (label: LabelWithStateAndSuggestions) => {
  if (label.fuseMatch) {
    const a = label.fuseMatch.indices;
    const str = label.code;

    const characters = a.reduce((aggr, value) => {
      const copy = [...aggr];
      for (let i = value[0]; i <= value[1]; i++) {
        copy[i] = 1;
      }
      return copy;
    }, Array<number>(str.length).fill(0));

    let fromIndex = 0;
    let res = '';
    do {
      const indexOf1 = characters.indexOf(1, fromIndex);
      if (indexOf1 === -1) { // no more highlights
        if (fromIndex < str.length) {
          res = res.concat(str.substring(fromIndex));
        }
        break;
      }
      if (indexOf1 > fromIndex) { // we are skipping over non-highlight characters
        res = res.concat(`${str.substring(fromIndex, indexOf1)}`);
      }
      const indexOf0 = characters.indexOf(0, indexOf1);
      if (indexOf0 === -1) { // highlight is at the end of the string
        res = res.concat(`<span class="lb-search-highlight">${str.substring(indexOf1)}</span>`);
        break;
      } else { // more non-highlight characters after this highlight, continue
        res = res.concat(`<span class="lb-search-highlight">${str.substring(indexOf1, indexOf0)}</span>`);
        fromIndex = indexOf0;
      }
    } while (fromIndex < characters.length);
    return res;
  }
  return label.code;
};
